import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { isEducation, isSubscriptionActivated, isTrialSubscription } from 'common/utils/permissions'
import Mixpanel from 'common/services/Mixpanel'

import Modal from 'components/Modal/Modal'
import LoaderEllipsis from 'components/LoaderEllipsis/LoaderEllipsis'

import { API__COMMON, API__PRODUCTS, API__USER } from 'api'

import { ORGANIZATIONS__REFRESH_SELECTED_SUBSCRIPTION, USER__SET_DETAILS, USER__SET_SUBSCRIPTIONS } from 'store/actions'

import Preloader from './components/Preloader/Preloader'

import ChooseGroup from './views/ChooseGroup/ChooseGroup'
import ChooseRole from './views/ChooseRole/ChooseRole'
import Contacts from './views/Contacts/Contacts'
import WhatIs from './views/WhatIs/WhatIs'
import ChooseEducationInterests from './views/ChooseInterests/Education/ChooseInterests'
import ChooseBusinessInterests from './views/ChooseInterests/Business/ChooseInterests'

import { VIEWS, BUSINESS_STEPS_COUNT, EDUCATION_STEPS_COUNT } from './constants'
import { isEducationGroup } from './utils'

import styles from './NewUserModal.module.scss'

const NewUserModal = ({ onClose }) => {
    const { t } = useTranslation()
    const [isReady, setIsReady] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const dispatch = useDispatch()
    const { user_details, organizations } = useSelector(state => state)
    const [interestsTags, setInterestsTags] = useState([])
    const [stepsCount, setStepsCount] = useState(0)

    const [groups, setGroups] = useState([])

    const [selectedGroup, seSelectedGroup] = useState(null)

    const [selectedRoleId, setSelectedRoleId] = useState(null)
    const [selectedInterests, setSelectedInterests] = useState([])

    const [view, setView] = useState(VIEWS.SELECT_USER_GROUP)

    useEffect(() => {
        getInitialData()
    }, [])

    useEffect(() => {
        if (!selectedGroup) {
            setStepsCount(0)
            return
        }
        setStepsCount(isEducationGroup(selectedGroup) ? EDUCATION_STEPS_COUNT : BUSINESS_STEPS_COUNT)
    }, [selectedGroup])

    const getInitialData = async () => {
        try {
            const [interestsData, groupsData] = await Promise.all([
                API__USER.GET_INTERESTS_TAGS(),
                API__COMMON.GET_USERS_PROJECTS_ROLES(),
            ])

            setInterestsTags(interestsData)
            setGroups(groupsData)
            setIsReady(true)
        } catch (err) {
            console.error(err)
        }
    }

    const onHandleSelectGroup = group => {
        seSelectedGroup(group)
        setView(VIEWS.SELECT_USER_ROLE)
    }
    const onHandleResetGroup = () => {
        seSelectedGroup(null)
        setView(VIEWS.SELECT_USER_GROUP)
    }
    const onHandleSelectRole = role => {
        setSelectedRoleId(role.id)

        if (isEducationGroup(selectedGroup)) {
            setView(VIEWS.SELECT_EDUCATION_INTERESTS)
        } else {
            setView(VIEWS.SELECT_BUSINESS_INTERESTS)
        }
    }
    const onHandleSelectInterests = interests => {
        setSelectedInterests(interests)

        if (isEducationGroup(selectedGroup)) {
            setView(VIEWS.WHAT_IS)
        } else {
            setView(VIEWS.BUSINESS_CONTACTS)
        }
    }

    const onSaveData = async () => {
        try {
            setIsSaving(true)
            await setUserRole(selectedRoleId)
            await setUserInterests(selectedInterests)

            onClose(!isEducationGroup(selectedGroup))
        } catch (err) {
            console.error(err)
            setIsSaving(false)
        }
    }

    const setUserRole = async roleId => {
        const updatedUser = await API__USER.SET_USER_ROLE(user_details.id, roleId || selectedRoleId)
        dispatch(USER__SET_DETAILS(updatedUser))

        if (!isSubscriptionActivated() && isTrialSubscription()) {
            const { selectedOrganizationId } = organizations
            if (isEducation()) await API__PRODUCTS.ACTIVATE_EDUCATION_FREE(selectedOrganizationId)
            else await API__PRODUCTS.ACTIVATE_TRIAL(selectedOrganizationId)

            Mixpanel.track('User role chosen', { role: roleId || selectedRoleId })
            await dispatch(ORGANIZATIONS__REFRESH_SELECTED_SUBSCRIPTION())
            await dispatch(USER__SET_SUBSCRIPTIONS(await API__USER.GET_SUBSCRIPTIONS(user_details.id)))
        }
    }
    const setUserInterests = async interests => {
        const updatedUser = await API__USER.SET_USER_INTERESTS(user_details.id, interests)
        dispatch(USER__SET_DETAILS(updatedUser))
    }

    return (
        <Modal closeOnEsc={false} closeOnOverlayClick={false}>
            <div className={styles.newUserModal}>
                {!isReady ? (
                    <Preloader />
                ) : (
                    <>
                        {view === VIEWS.SELECT_USER_GROUP && (
                            <ChooseGroup onSelect={onHandleSelectGroup} list={groups} />
                        )}

                        {view === VIEWS.SELECT_USER_ROLE && (
                            <ChooseRole
                                group={selectedGroup}
                                onSelect={onHandleSelectRole}
                                list={groups}
                                onBack={onHandleResetGroup}
                                currentStep={1}
                                stepsCount={stepsCount}
                            />
                        )}

                        {view === VIEWS.SELECT_BUSINESS_INTERESTS && (
                            <ChooseBusinessInterests
                                tags={interestsTags}
                                currentStep={2}
                                stepsCount={stepsCount}
                                onSelect={onHandleSelectInterests}
                            />
                        )}
                        {view === VIEWS.SELECT_EDUCATION_INTERESTS && (
                            <ChooseEducationInterests
                                tags={interestsTags}
                                currentStep={2}
                                stepsCount={stepsCount}
                                onSelect={onHandleSelectInterests}
                            />
                        )}

                        {view === VIEWS.WHAT_IS && (
                            <WhatIs
                                currentStep={4}
                                stepsCount={stepsCount}
                                onNext={() => setView(VIEWS.EDUCATION_CONTACTS)}
                            />
                        )}

                        {view === VIEWS.BUSINESS_CONTACTS && (
                            <Contacts
                                descriptionTitle={t('Watch our welcome video that will help you get started')}
                                isBusiness={true}
                                selectedInterests={selectedInterests}
                                currentStep={3}
                                stepsCount={stepsCount}
                                onNext={onSaveData}
                                nextButtonText={t('Start my free trial')}
                            />
                        )}
                        {view === VIEWS.EDUCATION_CONTACTS && (
                            <Contacts
                                descriptionTitle={t('Watch our short tutorial on how to create your first project')}
                                isBusiness={false}
                                selectedInterests={selectedInterests}
                                currentStep={5}
                                stepsCount={stepsCount}
                                onNext={onSaveData}
                                nextButtonText={t('Let’s start!')}
                            />
                        )}
                    </>
                )}
                {isSaving && (
                    <div className={styles.loader}>
                        <LoaderEllipsis />
                    </div>
                )}
            </div>
        </Modal>
    )
}

export default NewUserModal
